import React from "react";
import Box from "@mui/material/Box";
import SvgIcon from "@mui/material/SvgIcon";

/**
 * Picture-in-Picture Icon Component
 * A custom icon showing a square with rounded corners containing a smaller square in the bottom-left
 */
export const PictureInPictureIcon = (props) => {
  const { fillColor, ...otherProps } = props;
  return (
    <SvgIcon {...otherProps} viewBox="0 0 24 24">
      {/* Outer square with rounded corners */}
      <rect
        x="1"
        y="1"
        width="22"
        height="22"
        rx="2"
        ry="2"
        fill={fillColor || "none"}
        stroke="white"
        strokeWidth="1.5"
      />
      {/* Inner smaller square in bottom-left */}
      <rect x="4" y="14" width="9" height="8" rx="1.5" ry="1.5" fill="white" />
    </SvgIcon>
  );
};

/**
 * Picture-in-Max-Picture Icon Component
 * A custom icon showing a square with rounded corners containing a smaller square on the left side
 */
export const PictureInMaxPictureIcon = (props) => {
  const { fillColor, ...otherProps } = props;
  return (
    <SvgIcon {...otherProps} viewBox="0 0 24 24">
      {/* Outer square with rounded corners */}
      <rect
        x="1"
        y="1"
        width="22"
        height="22"
        rx="2"
        ry="2"
        fill={fillColor || "none"}
        stroke="white"
        strokeWidth="1.5"
      />
      {/* Inner smaller square in bottom-left */}
      <rect x="4" y="1" width="9" height="22" rx="1.5" ry="1.5" fill="white" />
    </SvgIcon>
  );
};

/**
 * Picture-in-Picture Circle Icon Component
 * A custom icon showing a square with rounded corners containing a smaller circle in the bottom-left
 */
export const PictureInPictureCircleIcon = (props) => {
  const { fillColor, ...otherProps } = props;
  return (
    <SvgIcon {...otherProps} viewBox="0 0 24 24">
      {/* Outer square with rounded corners */}
      <rect
        x="1"
        y="1"
        width="22"
        height="22"
        rx="2"
        ry="2"
        fill={fillColor || "none"}
        stroke="currentColor"
        strokeWidth="1.5"
      />
      {/* Inner circle in bottom-left */}
      <circle cx="8.5" cy="18" r="4" fill="currentColor" />
    </SvgIcon>
  );
};

/**
 * Circular Progress Icon Component
 * A custom icon showing a circle with white border, dark interior, central white dot, and lighter gray pie-slice segment
 */
export const CircularProgressIcon = (props) => {
  const { yaw, fov } = props;

  const size = 60;

  return (
    <Box sx={{ width: size * 1.1, height: size * 1.1 }}>
      <svg width="90%" height="90%" viewBox={`0 0 ${size} ${size}`}>
        {(() => {
          const center = size / 2;
          const gap = size * 0.06; // adjust gap size here

          const borderWidth = size * 0.04; // same as strokeWidth
          const outerRadius = size / 2 - borderWidth / 2;
          const innerRadius = size * 0.15;
          const sweepInnerRadius = innerRadius + gap;

          const sweepAngle = fov ?? 60;

          const angleToRad = (angle) => (Math.PI / 180) * angle;

          // Centered FOV
          const startAngle = -sweepAngle / 2 - 90;
          const endAngle = sweepAngle / 2 - 90;

          const xOuterStart =
            center + outerRadius * Math.cos(angleToRad(startAngle));
          const yOuterStart =
            center + outerRadius * Math.sin(angleToRad(startAngle));

          const xOuterEnd =
            center + outerRadius * Math.cos(angleToRad(endAngle));
          const yOuterEnd =
            center + outerRadius * Math.sin(angleToRad(endAngle));

          const xInnerStart =
            center + sweepInnerRadius * Math.cos(angleToRad(endAngle));
          const yInnerStart =
            center + sweepInnerRadius * Math.sin(angleToRad(endAngle));

          const xInnerEnd =
            center + sweepInnerRadius * Math.cos(angleToRad(startAngle));
          const yInnerEnd =
            center + sweepInnerRadius * Math.sin(angleToRad(startAngle));

          const largeArcFlag = sweepAngle > 180 ? 1 : 0;

          const sweepPath = `
        M ${xOuterStart} ${yOuterStart}
        A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${xOuterEnd} ${yOuterEnd}
        L ${xInnerStart} ${yInnerStart}
        A ${sweepInnerRadius} ${sweepInnerRadius} 0 ${largeArcFlag} 0 ${xInnerEnd} ${yInnerEnd}
        Z
      `;

          return (
            <>
              {/* Outer Light Circle */}
              <circle
                cx={center}
                cy={center}
                r={outerRadius}
                strokeWidth={borderWidth}
                fill="#b9efb6"
              />

              {/* Rotating Sweep */}
              <g
                style={{
                  transformOrigin: `${center}px ${center}px`,
                  transform: `rotate(${yaw ?? 0}deg)`,
                  transition: "transform 0.05s linear",
                }}
              >
                <path d={sweepPath} fill="#1BC800" opacity="0.80" />
              </g>

              {/* Inner Circle */}
              <circle
                cx={center}
                cy={center}
                r={innerRadius}
                fill="#1BC800"
              />

              {/* Borders */}
              <circle
                cx={center}
                cy={center}
                r={outerRadius}
                fill="none"
                stroke="#0c8f26"
                strokeWidth={size * 0.04}
              />
              <circle
                cx={center}
                cy={center}
                r={innerRadius}
                fill="none"
                stroke="#0c8f26"
                strokeWidth={size * 0.04}
              />
            </>
          );
        })()}
      </svg>
    </Box>
  );
};

